const observers = new WeakMap();
const viewportObservers = new WeakMap();

/** Read every participating component before any of them changes layout. */
export function createLayoutObserver(callback, view = window) {
    let shared = observers.get(view);
    if (!shared) {
        const targets = new Map();
        const observer = new view.ResizeObserver((entries) => {
            const deliveries = new Map();
            entries.forEach((entry) => {
                targets.get(entry.target)?.forEach((listener) => {
                    const delivery = deliveries.get(listener) || [];
                    delivery.push(entry);
                    deliveries.set(listener, delivery);
                });
            });
            const updates = [...deliveries].map(([listener, delivery]) => listener(delivery));
            updates.forEach((apply) => apply?.());
        });
        shared = { targets, observer };
        observers.set(view, shared);
    }
    const observed = new Set();
    const unobserve = (target) => {
        observed.delete(target);
        const listeners = shared.targets.get(target);
        listeners?.delete(callback);
        if (listeners?.size) return;
        shared.targets.delete(target);
        shared.observer.unobserve(target);
    };
    return {
        observe(target) {
            if (observed.has(target)) return;
            observed.add(target);
            let listeners = shared.targets.get(target);
            if (!listeners) {
                listeners = new Set();
                shared.targets.set(target, listeners);
                shared.observer.observe(target);
            }
            listeners.add(callback);
        },
        unobserve,
        refresh(target) {
            if (!observed.has(target)) return;
            // A fresh native delivery also covers CSS breakpoints that do not
            // change this target's dimensions. Do not measure during resize.
            shared.observer.unobserve(target);
            shared.observer.observe(target);
        },
        disconnect() {
            observed.forEach(unobserve);
        },
    };
}

/** Share native viewport snapshots without measuring after a media/slide write. */
export function createViewportObserver(callback, view = window) {
    let shared = viewportObservers.get(view);
    if (!shared) {
        const targets = new Map();
        const deliver = (entries) => {
            const deliveries = new Map();
            entries.forEach((entry) => {
                targets.get(entry.target)?.forEach((listener) => {
                    const delivery = deliveries.get(listener) || [];
                    delivery.push(entry);
                    deliveries.set(listener, delivery);
                });
            });
            const updates = [...deliveries].map(([listener, delivery]) => listener(delivery));
            updates.forEach((apply) => apply?.());
        };
        const observer = new view.IntersectionObserver((entries) => {
            // All style reads precede every participating slider/video write.
            const snapshots = entries.map((entry) => ({
                target: entry.target,
                visible:
                    entry.isIntersecting &&
                    entry.intersectionRatio > 0 &&
                    entry.boundingClientRect.width > 0 &&
                    entry.boundingClientRect.height > 0 &&
                    view.getComputedStyle(entry.target).visibility === 'visible',
            }));
            deliver(snapshots);
        });
        const invalidate = () => {
            // A playback promise or autoplay timer can complete before the next
            // native delivery. Pause until fresh bounds are available after a
            // scroll/resize instead of forcing layout at that boundary.
            observer.takeRecords();
            // Distinguish stale bounds from a confirmed viewport exit.
            deliver(
                [...targets.keys()].map((target) => ({
                    target,
                    visible: false,
                    invalidated: true,
                })),
            );
            targets.forEach((_, target) => {
                observer.unobserve(target);
                observer.observe(target);
            });
        };
        view.addEventListener('scroll', invalidate, { capture: true, passive: true });
        view.addEventListener('resize', invalidate, { passive: true });
        const document = view.document;
        document?.addEventListener('visibilitychange', invalidate);
        shared = { targets, observer, invalidate, document };
        viewportObservers.set(view, shared);
    }
    const observed = new Set();
    const unobserve = (target) => {
        observed.delete(target);
        const listeners = shared.targets.get(target);
        listeners?.delete(callback);
        if (listeners?.size) return;
        shared.targets.delete(target);
        shared.observer.unobserve(target);
        if (shared.targets.size) return;
        view.removeEventListener('scroll', shared.invalidate, { capture: true });
        view.removeEventListener('resize', shared.invalidate);
        shared.document?.removeEventListener('visibilitychange', shared.invalidate);
        shared.observer.disconnect();
        viewportObservers.delete(view);
    };
    return {
        observe(target) {
            if (observed.has(target)) return;
            observed.add(target);
            let listeners = shared.targets.get(target);
            if (!listeners) {
                listeners = new Set();
                shared.targets.set(target, listeners);
                shared.observer.observe(target);
            }
            listeners.add(callback);
        },
        unobserve,
        disconnect() {
            observed.forEach(unobserve);
        },
    };
}
